import { useTranslation } from 'react-i18next';
import { AlertTriangle, Star } from 'lucide-react';
import { cn } from 'lib/utils';
import { Checkbox } from 'components/ui/checkbox';
import { technicianFullName } from 'pages/AdminTechnicians/csv';
import { initials } from 'components/rating/RatingForm';
import type { InterventionCandidate } from 'api/interventionRequestsApi';
import { SKILL_TAG } from './helpBlock';

/**
 * One proposed technician, ticked or not — several are solicited in one go since L5b (D45), so
 * the card carries a checkbox and no send button of its own: the panel's footer sends them all.
 *
 * **The rating is shown and does not enter the score**, as the mock-up has it: making it count
 * would be a product decision nobody has taken.
 *
 * **No `X km` and no `~Y min`.** The distance is measured from the default address, which L2c
 * has not delivered — the API sends `distanceKm: null`, and a fabricated figure would be a
 * ranking that looks precise and is not.
 *
 * A technician already solicited stays at his own rank, **disabled and marked** rather than
 * hidden or moved to the top: the reader sees why he cannot be ticked again.
 */

interface CandidateCardProps {
  candidate: InterventionCandidate;
  /** The mission's skills, so an uncovered one can be marked rather than merely absent. */
  missionSpecialtyIds: number[];
  /** The best candidate nobody has tried yet — the design's `isBest`. */
  recommended: boolean;
  /** Already solicited on this mission: the send would be refused, so the row is not tickable. */
  alreadySolicited: boolean;
  selected: boolean;
  onToggle: (candidate: InterventionCandidate) => void;
}

const CandidateCard = ({
  candidate,
  missionSpecialtyIds,
  recommended,
  alreadySolicited,
  selected,
  onToggle,
}: CandidateCardProps) => {
  const { t, i18n } = useTranslation();
  const uncovered = new Set(candidate.uncoveredSpecialtyIds);
  const name = technicianFullName(candidate) || candidate.username;
  const boxId = `assign-candidate-${candidate.id}`;

  return (
    <li
      className={cn(
        'rounded-xl border p-3',
        selected && 'border-ring ring-1 ring-ring',
        alreadySolicited && 'bg-muted/50 opacity-60'
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        <Checkbox
          id={boxId}
          className="mt-1 h-[18px] w-[18px] shrink-0"
          checked={selected}
          disabled={alreadySolicited}
          aria-label={name}
          onCheckedChange={() => onToggle(candidate)}
        />
        {/* The design's avatar: initials, reusing the helper the rating sheet already owns. */}
        <span
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-zinc-100 text-[12px] font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
          aria-hidden="true"
        >
          {initials(name)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <label
              htmlFor={boxId}
              className={cn(
                'truncate text-[13.5px] font-medium text-zinc-900 dark:text-zinc-50',
                !alreadySolicited && 'cursor-pointer'
              )}
            >
              {name}
            </label>
            {/* The directory's own marker, same triangle and same sr-only label. A **warning, not
                a block**: he is still tickable and still sendable — he is solicited by e-mail and
                can accept. Only the Jira participant write is at stake, so the wording here is the
                drawer's own and not the listing's « cannot be sent ». */}
            {!candidate.externalId && (
              <span
                className="inline-flex shrink-0 items-center text-amber-600 dark:text-amber-400"
                title={t('No Jira account linked: if he accepts, he cannot be added to the ticket participants. The solicitation can still be sent.')}
              >
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                <span className="sr-only">{t('Mobile account not provisioned')}</span>
              </span>
            )}
            {/* `isBest` in the design: the one the ranking puts first among those never asked. */}
            {recommended && (
              <span className="shrink-0 rounded-[5px] bg-emerald-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
                {t('Recommended')}
              </span>
            )}
            {/* Why the box is dead, on the row itself — a disabled control alone is a defect. */}
            {alreadySolicited && (
              <span className="ml-auto shrink-0 text-[11.5px] text-muted-foreground">
                {t('Already solicited')}
              </span>
            )}
          </div>
          {/*
            One line, star first, as the mock-up's card draws it — not the directory's stacked
            « 4,6 / 5 » over « N avis ». `RatingCell` keeps that shape for the directory, whose
            own mock-up has no star in the list cell; this card is the only place one is drawn.
            Still never a 0: an unrated technician says so in words.
          */}
          <p className="mt-1 flex items-center gap-1.5 truncate text-[12px] text-muted-foreground">
            {candidate.ratings.avg == null || candidate.ratings.count === 0 ? (
              <span className="text-muted-foreground/70">{t('Not rated yet')}</span>
            ) : (
              <>
                <Star className="h-3 w-3 shrink-0 fill-amber-500 text-amber-500" aria-hidden="true" />
                <span>
                  {candidate.ratings.avg.toLocaleString(i18n.language, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                </span>
                <span className="text-muted-foreground/70">
                  {t('{{count}} rating', { count: candidate.ratings.count })}
                </span>
              </>
            )}
            {candidate.profile.company && <span className="truncate">· {candidate.profile.company}</span>}
          </p>

          {/* What he has already taken that day (D69) — a fact, not a warning: nothing here ranks
              or blocks. From 1 only: `0` and `null` both say nothing, for two different reasons. */}
          {!!candidate.sameDayMissions && (
            <p className="mt-1 text-[12px] text-muted-foreground">
              {t('{{count}} mission already accepted that day', { count: candidate.sameDayMissions })}
            </p>
          )}

          {/* No availability pill (D62): this list holds only technicians free on the mission's day,
              so a badge saying so on every row distinguishes nothing. It stays in the search
              panel, which deliberately filters neither perimeter nor availability (D20). */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {candidate.specialties.length === 0 ? (
              <span className="text-[12px] text-muted-foreground/70">{t('No skill declared')}</span>
            ) : (
              candidate.specialties.map((specialty) => (
                <span key={specialty.id} className={SKILL_TAG}>
                  {specialty.name}
                </span>
              ))
            )}
          </div>

          {/*
            What the mission asks for and this technician does not carry. Named rather than merely
            absent from the list above: `uncovered` is what the score is built on, so the reader can
            see why one card sits below another instead of taking the order on faith.
          */}
          {missionSpecialtyIds.some((id) => uncovered.has(id)) && (
            <p className="mt-2 text-[12px] text-amber-700 dark:text-amber-400">
              {t('{{count}} required skill not covered', { count: candidate.uncoveredSpecialtyIds.length })}
            </p>
          )}
        </div>
      </div>
    </li>
  );
};

export default CandidateCard;
